import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldAlert, Eye, EyeOff } from "lucide-react";

export default function ForcePasswordModal() {
  const { user, refresh, logout } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  if (!user || !user.must_change_password) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) return toast.error("Yeni şifre en az 8 karakter olmalı");
    if (nw !== nw2) return toast.error("Yeni şifreler eşleşmiyor");
    if (nw === cur) return toast.error("Yeni şifre mevcut şifreyle aynı olamaz");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      if (data.token) localStorage.setItem("madymark_token", data.token);
      toast.success("Şifreniz güncellendi. Hoş geldiniz!");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Şifre değiştirilemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm grid place-items-center p-4" data-testid="force-pw-modal">
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-amber-100 grid place-items-center">
            <ShieldAlert className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-600 font-bold">Zorunlu Adım</div>
            <div className="font-display text-xl font-extrabold text-slate-900 dark:text-slate-100">Şifrenizi Belirleyin</div>
          </div>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-6">
          İlk girişte güvenliğiniz için varsayılan şifreyi değiştirmeniz gerekir.
          Bu şifre sadece size özel olacaktır — kimseyle paylaşmayın.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Mevcut Şifre" type={show ? "text" : "password"} value={cur} onChange={setCur}
                 testId="fp-cur" onToggle={() => setShow(!show)} show={show} />
          <Field label="Yeni Şifre (min. 8 karakter)" type={show ? "text" : "password"} value={nw} onChange={setNw}
                 testId="fp-new" />
          <Field label="Yeni Şifre (tekrar)" type={show ? "text" : "password"} value={nw2} onChange={setNw2}
                 testId="fp-new2" />
          <button type="submit" disabled={busy} data-testid="fp-submit"
            className="w-full py-3 rounded-lg bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] disabled:opacity-60 transition">
            {busy ? "Kaydediliyor..." : "Şifreyi Değiştir ve Devam Et"}
          </button>
        </form>
        <button onClick={logout} data-testid="fp-logout"
          className="mt-4 w-full text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-300">
          Çıkış Yap
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type, testId, onToggle, show }) {
  return (
    <div>
      <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">{label}</label>
      <div className="relative">
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
          required autoComplete="new-password"
          className="w-full mt-1 px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-[#1E2A9C]/30 outline-none" />
        {onToggle && (
          <button type="button" onClick={onToggle} data-testid="fp-toggle-show"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
